import { Box, Link, MenuItem, Typography } from '@mui/material'
import AddRoundedIcon from '@mui/icons-material/AddRounded'
import { useDepartments } from '../../hooks/useDepartments'
import CustomMultiSelectChip from '../common/CustomMultiSelectChip'
import { UserState } from './userReducer'
import { useFormContext } from 'react-hook-form'
import { useEffect, useState } from 'react'
import { Department } from '../../../generated'
import { CaseContextProvider } from '../../context'
import DepartmentModal from '../departments/DepartmentModal'

const SelectDepartments = ({ state }: { state?: UserState }) => {
  const {
    departments,
    setPage,
    searchTerm,
    setSearchTerm,
    addSelectedDepartments,
    addCreatedDepartment,
    removeSelectedDepartments
  } = useDepartments({})
  const { currentUser } = state || {}
  const [newDepartmentName, setNewDepartmentName] = useState<string | null>(null)

  const { watch, setValue } = useFormContext()

  useEffect(() => {
    const selectedDepartmentIds = watch('departmentIds') || []
    if (selectedDepartmentIds.length > 0) {
      addSelectedDepartments(selectedDepartmentIds)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [addSelectedDepartments])

  const getChipLabel = (value: string) => {
    const department = departments.find(department => department.id === value)

    if (!department) {
      const foundDept = currentUser?.deptToUsers?.find(deptToUser => {
        if (deptToUser?.departmentId === value) return deptToUser.department
        return null
      })

      return (
        <Box display="flex" alignItems="center" gap={1}>
          <Typography variant="body1" fontSize={12}>
            {foundDept?.department.name || 'Loading Department'}
          </Typography>
        </Box>
      )
    } else {
      return (
        <Box display="flex" alignItems="center" gap={1}>
          <Typography variant="body1" fontSize={12}>
            {department?.name}
          </Typography>
        </Box>
      )
    }
  }

  const getLabel = (value: string) => {
    const department = departments.find(dept => dept.id === value)
    if (department) {
      return department.name
    }

    const foundDept = currentUser?.deptToUsers?.find(deptToUser => deptToUser?.departmentId === value)

    return foundDept?.department.name || 'Loading Department'
  }

  const handleSelectionChange = (newSelectedIds: string[], previousSelectedIds: string[]) => {
    const deselectedIds = previousSelectedIds.filter(id => !newSelectedIds.includes(id))

    if (deselectedIds.length > 0) removeSelectedDepartments(deselectedIds)
  }

  const handleDepartmentCreated = (department: Pick<Department, 'id' | 'name'>) => {
    addCreatedDepartment(department as Department)
    const selectedDepartmentIds: string[] = watch('departmentIds') || []
    setValue('departmentIds', [...selectedDepartmentIds, department.id], { shouldDirty: true, shouldValidate: true })
  }

  return (
    <>
      <CustomMultiSelectChip
        name="departmentIds"
        label="Departments"
        noOptionsText="No Departments"
        setPage={setPage}
        setSearchTerm={setSearchTerm}
        chipLabel={getChipLabel}
        handleChange={handleSelectionChange}
        children={
          departments.length > 0 ? (
            departments?.map(item => {
              const { id, name } = item ?? {}
              return (
                <MenuItem value={id} key={id}>
                  <Box display={'flex'} alignItems={'center'} gap={1}>
                    <Typography variant="body1">{name}</Typography>
                  </Box>
                </MenuItem>
              )
            })
          ) : (
            <Typography variant="body1" padding={'0 8px'}>
              No departments
            </Typography>
          )
        }
        getLabel={getLabel}
      />

      <Box mt={-0.5} mb={2}>
        <Link
          component="button"
          type="button"
          underline="hover"
          onClick={() => setNewDepartmentName(searchTerm.trim())}
          sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5, fontSize: 13, fontWeight: 500 }}
        >
          <AddRoundedIcon sx={{ fontSize: 16 }} />
          Add New Department
        </Link>
      </Box>

      {newDepartmentName !== null && (
        <CaseContextProvider>
          <DepartmentModal
            isOpen
            initialName={newDepartmentName}
            handleClose={() => setNewDepartmentName(null)}
            onCreated={handleDepartmentCreated}
          />
        </CaseContextProvider>
      )}
    </>
  )
}

export default SelectDepartments
